import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";

import LoadingLayout from "./Loading";
import { logIn } from "../store/auth/slice";
import { OAUTH_STATE_KEY, OAUTH_FROM_KEY } from "./Auth";

/**
 * Handles the OAuth redirect callback from Google.
 *
 * Google redirects to /auth/callback with `code` and `state` as URL search
 * params. This component:
 *   1. Verifies `state` matches the value stored in localStorage (CSRF check)
 *   2. Dispatches logIn with the authorization code
 *   3. Navigates to the original destination once logged in
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Guard against StrictMode double-invoke or multiple renders firing the
  // login dispatch more than once.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const returnedState = params.get("state");

    // No code in the URL — this is a stale remount after the callback already
    // processed (e.g. React re-renders after login). Redirect silently.
    if (!code) {
      navigate("/", { replace: true });
      return;
    }

    const storedRaw = localStorage.getItem(OAUTH_STATE_KEY);
    const fromRaw = localStorage.getItem(OAUTH_FROM_KEY);
    const from = fromRaw ? JSON.parse(fromRaw) : { pathname: "/" };

    localStorage.removeItem(OAUTH_STATE_KEY);
    localStorage.removeItem(OAUTH_FROM_KEY);

    // Strip OAuth params from the URL immediately so any further remounts of
    // this component see no code and bail out silently above.
    window.history.replaceState({}, "", "/auth/callback");

    const stored = storedRaw ? JSON.parse(storedRaw) : null;
    const expectedState = stored?.state ?? null;
    const stateAge = stored ? Date.now() - stored.ts : Infinity;
    const STATE_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

    if (!expectedState || returnedState !== expectedState || stateAge > STATE_MAX_AGE_MS) {
      console.error("OAuth state mismatch — possible CSRF attack.");
      navigate("/login", { replace: true });
      return;
    }

    // logIn expects an AuthCodeSuccessResponse shape; only `code` is used.
    (dispatch(logIn({ code } as any)) as any)
      .then(() => {
        navigate(from, { replace: true });
      })
      .catch(() => {
        navigate("/login", { replace: true });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <LoadingLayout fullscreen />;
};

export default AuthCallback;
